import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import configData from '../config.json';
import {
  DELIVERY_CHANGED_EVENT,
  DELIVERY_HANDOFF_EVENT,
  deliveryFromSeed,
  forgetOwnDelivery,
  forgetSignedInDeliveries,
  isDeliveryActive,
  mergeDeliveryStatus,
  projectActiveDeliveries,
  readDismissedDeliveries,
  readOwnDeliveries,
  sameDelivery,
  saveDismissedDeliveries,
} from '../utils/deliveryTracking';

// While a delivery is on screen its status is re-read on this cadence — the
// panel's 15 s is the app's DELIVERY_TRACKING_POLL_INTERVAL. The button alone
// polls more slowly: most web guests never allow push, which is what keeps the
// app's button current, so without this the step would never move.
export const PANEL_POLL_MS = 15000;
export const BUTTON_POLL_MS = 30000;

// A list read that lost the race to a newer single-order write is re-run, but
// only this often in a row, so the two can never ping-pong.
const MAX_RECONCILE_ATTEMPTS = 2;

// /orders/checkstatus needs no login: a guest who is not signed in sends none.
const authHeaders = (token) =>
  token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;

const newestFirst = (list) =>
  [...list].sort((a, b) => new Date(b.placedAt).getTime() - new Date(a.placedAt).getTime());

/**
 * The guest's Wolt deliveries still worth showing (the app's
 * useActiveDeliveries + activeDelivery.ts): a signed-in guest's from their
 * account, and — signed in or not — any the menu page handed over in this
 * browser as the venue accepted it (utils/deliveryTracking, the hand-over).
 *
 * The list endpoint and a hand-over are what ADD a delivery; /orders/checkstatus
 * refreshes one already known. The list is read when the token changes, when
 * the tab comes back, and when an order's delivery is announced as changed.
 */
export const useActiveDeliveries = (token, { panelOpen = false, enabled = true } = {}) => {
  const [deliveries, setDeliveries] = useState([]);
  // The last delivery handed over to be opened on, as { orderId, seq }.
  const [handoff, setHandoff] = useState(null);

  const tokenRef = useRef(token);
  tokenRef.current = token;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const deliveriesRef = useRef(deliveries);
  deliveriesRef.current = deliveries;
  const dismissedRef = useRef(readDismissedDeliveries());
  // Bumped by every write that is not a list read. A list read that started
  // before one landed is stale — writing it could bring back a delivery a
  // refresh has just retired — so it reads again instead.
  const versionRef = useRef(0);
  const reconcileRef = useRef({ inFlight: false, queued: false, attempt: 0 });
  const handoffSeqRef = useRef(0);
  // One status read per order at a time, however many ask for it at once.
  const refreshingRef = useRef(new Set());

  const commit = useCallback((next) => {
    versionRef.current += 1;
    deliveriesRef.current = next;
    setDeliveries(next);
  }, []);

  /**
   * One order's latest /orders/checkstatus answer, applied: the delivery is
   * shown, moved on, or retired — and one handed over here is forgotten once
   * it is over. `base` is what to build on when it is not shown yet.
   * Returns whether it is shown afterwards.
   */
  const apply = useCallback(
    (orderId, data, base) => {
      const current = deliveriesRef.current;
      const shown = current.find((d) => d.orderId === orderId);
      const existing = shown || base;
      if (!existing) return false;
      // Not accepted yet (the endpoint answers nothing for that): nothing to show.
      if (!shown && !data.type) return false;

      const { delivery, orderStatus } = mergeDeliveryStatus(existing, data);
      const stillActive = isDeliveryActive(delivery, orderStatus, false);
      if (!stillActive) forgetOwnDelivery(orderId);

      // Nothing moved: leave the list (and the version) alone, so a list read
      // in flight is not thrown away for nothing.
      if (stillActive && shown && sameDelivery(shown, delivery)) return true;
      if (!stillActive && !shown) return false;

      if (!stillActive) {
        commit(current.filter((d) => d.orderId !== orderId));
      } else if (shown) {
        commit(current.map((d) => (d.orderId === orderId ? delivery : d)));
      } else {
        commit(newestFirst([...current, delivery]));
      }
      return stillActive;
    },
    [commit],
  );

  const refresh = useCallback(
    async (orderId, base) => {
      if (!enabledRef.current || refreshingRef.current.has(orderId)) return;
      refreshingRef.current.add(orderId);
      const requestToken = tokenRef.current;
      try {
        const { data } = await axios.get(
          `${configData.SERVER_URL}/orders/checkstatus?id=${orderId}`,
          authHeaders(requestToken),
        );
        if (!data || tokenRef.current !== requestToken) return;
        apply(orderId, data, base);
      } catch (e) {
        // A failed read leaves the last known state rather than blanking a
        // delivery mid-flight over a network blip.
      } finally {
        refreshingRef.current.delete(orderId);
      }
    },
    [apply],
  );

  const refreshOwn = useCallback(
    (seeds) => seeds.forEach((seed) => refresh(seed.orderId, deliveryFromSeed(seed))),
    [refresh],
  );

  const reconcile = useCallback(async () => {
    if (!enabledRef.current) return;
    const state = reconcileRef.current;
    if (state.inFlight) {
      state.queued = true;
      return;
    }
    const requestToken = tokenRef.current;

    // Not signed in (or signed out): only what was handed over in this browser
    // without signing in.
    if (!requestToken) {
      forgetSignedInDeliveries();
      const own = readOwnDeliveries();
      const ownIds = new Set(own.map((seed) => seed.orderId));
      const kept = deliveriesRef.current.filter((d) => ownIds.has(d.orderId));
      if (kept.length !== deliveriesRef.current.length) commit(kept);
      refreshOwn(own);
      return;
    }

    state.inFlight = true;
    state.attempt += 1;
    const versionBefore = versionRef.current;
    try {
      const { data } = await axios.get(
        `${configData.SERVER_URL}/orders/fetch/recent?forDinein=false&startdate=&enddate=&keyword=&includeLiveDelivery=true`,
        authHeaders(requestToken),
      );
      // Signed out or switched account while this was in flight.
      if (tokenRef.current !== requestToken) return;
      if (versionRef.current !== versionBefore) {
        if (state.attempt < MAX_RECONCILE_ATTEMPTS) state.queued = true;
        return;
      }
      // The account's list decides for the orders it can describe as deliveries
      // — an API that does not send `woltDelivery` describes none. Any other
      // order handed over here keeps its own refresh.
      const orders = Array.isArray(data) ? data : [];
      const listed = new Set(
        orders.filter((o) => o && 'woltDelivery' in o).map((o) => String(o._id)),
      );
      const unlisted = readOwnDeliveries().filter((seed) => !listed.has(seed.orderId));
      const next = newestFirst([
        ...projectActiveDeliveries(orders, Date.now(), new Set(dismissedRef.current)),
        ...deliveriesRef.current.filter((d) => unlisted.some((seed) => seed.orderId === d.orderId)),
      ]);
      deliveriesRef.current = next;
      setDeliveries(next);
      refreshOwn(unlisted);
    } catch (e) {
      // same policy as refresh
    } finally {
      state.inFlight = false;
      if (state.queued) {
        state.queued = false;
        reconcile();
      } else {
        state.attempt = 0;
      }
    }
  }, [commit, refreshOwn]);

  // Only a FAILED delivery is dismissible (the panel offers "Got it" for
  // nothing else); a running one retires itself when the courier arrives.
  const dismiss = useCallback(
    (orderId) => {
      if (!dismissedRef.current.includes(orderId)) {
        dismissedRef.current = [...dismissedRef.current, orderId];
        saveDismissedDeliveries(dismissedRef.current);
      }
      forgetOwnDelivery(orderId);
      commit(deliveriesRef.current.filter((d) => d.orderId !== orderId));
    },
    [commit],
  );

  // Signed in, out, or as someone else — or back from a page it stays off: the
  // list follows.
  useEffect(() => {
    reconcile();
  }, [token, enabled, reconcile]);

  // Back to the tab: an arbitrary amount of the delivery happened meanwhile.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') reconcile();
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [reconcile]);

  // An order's delivery changed (accepted, a Wolt push): refresh it if it is
  // shown, otherwise read the list — the only way a new one appears.
  useEffect(() => {
    const onChanged = (event) => {
      const { orderId, trackedOnly } = event?.detail || {};
      if (orderId && deliveriesRef.current.some((d) => d.orderId === String(orderId))) {
        refresh(String(orderId));
      } else if (!trackedOnly) {
        reconcile();
      }
    };
    window.addEventListener(DELIVERY_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(DELIVERY_CHANGED_EVENT, onChanged);
  }, [refresh, reconcile]);

  // The menu page handed a delivery over as its venue accepted it (and has
  // kept it in this browser). With the accepting status in hand it shows at
  // once. A signed-in guest's account then says what an order does not — the
  // venue's number, the short tracking link — so it is read straight away.
  useEffect(() => {
    const onHandOff = (event) => {
      const { seed, status, open } = event?.detail || {};
      if (!seed?.orderId || !enabledRef.current) return;

      const base = deliveryFromSeed(seed);
      if (!status) {
        refresh(seed.orderId, base);
      } else if (apply(seed.orderId, status, base) && open) {
        handoffSeqRef.current += 1;
        setHandoff({ orderId: seed.orderId, seq: handoffSeqRef.current });
      }
      if (tokenRef.current) reconcile();
    };
    window.addEventListener(DELIVERY_HANDOFF_EVENT, onHandOff);
    return () => window.removeEventListener(DELIVERY_HANDOFF_EVENT, onHandOff);
  }, [apply, refresh, reconcile]);

  // Keep what is shown moving. Keyed on the ids, not the objects: every
  // refresh writes new objects, and depending on them would restart the
  // interval on each one before it could ever fire.
  const ids = deliveries.map((d) => d.orderId).join(',');
  useEffect(() => {
    if (!ids) return undefined;
    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      ids.split(',').forEach((orderId) => refresh(orderId));
    };
    const interval = setInterval(tick, panelOpen ? PANEL_POLL_MS : BUTTON_POLL_MS);
    return () => clearInterval(interval);
  }, [ids, panelOpen, refresh]);

  return { deliveries, dismiss, refresh, reconcile, handoff };
};
